import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, MoreHorizontal, SlidersHorizontal } from "lucide-react";
import PullToRefresh from "./pull-to-refresh";
import BottomSheet from "./bottom-sheet";
import ActionSheet, { ActionSheetItem } from "./action-sheet";
import FloatingActionButton from "./floating-action-button";
import MobileIconButton from "./mobile-icon-button";
import MobileStatsStrip from "./mobile-stats-strip";
import MobileInsights from "./mobile-insights";
import { MobilePrimaryAction, StatItem } from "./types";
import { cn } from "../../lib/utils";

export interface MobilePageLayoutProps {
  title: string;
  description?: React.ReactNode;
  onRefresh?: () => unknown;
  primaryAction?: MobilePrimaryAction;
  actions?: Array<ActionSheetItem | false | null | undefined>;
  filters?: React.ReactNode;
  activeFilterCount?: number;
  onResetFilters?: () => void;
  /** Shown under the title, e.g. the active warehouse chip. */
  headerContent?: React.ReactNode;
  stats?: StatItem[];
  insights?: React.ReactNode;
  /** Show a back chevron instead of treating the page as a tab root. */
  backTo?: string | number;
  /** Custom back handler (e.g. to pass navigate state); takes precedence over `backTo`. */
  onBack?: () => void;
  children: React.ReactNode;
}

const COLLAPSE_AFTER = 56;

const MobilePageLayout: React.FC<MobilePageLayoutProps> = ({
  title,
  description,
  onRefresh,
  primaryAction,
  actions,
  filters,
  activeFilterCount,
  onResetFilters,
  headerContent,
  stats,
  insights,
  backTo,
  onBack,
  children,
}) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const lastScrollTop = useRef(0);

  const visibleActions = (actions ?? []).filter((action): action is ActionSheetItem => !!action);

  const handleScroll = (scrollTop: number) => {
    setIsCollapsed(scrollTop > COLLAPSE_AFTER);
    const delta = scrollTop - lastScrollTop.current;
    if (Math.abs(delta) > 6) setIsScrollingDown(delta > 0 && scrollTop > COLLAPSE_AFTER);
    lastScrollTop.current = scrollTop;
  };

  const goBack = () => {
    if (onBack) onBack();
    else if (typeof backTo === "number") navigate(backTo);
    else if (backTo) navigate(backTo);
  };

  const backButton = (onBack || backTo !== undefined) && (
    <MobileIconButton label="Back" onClick={goBack}>
      <ChevronLeft className="h-5 w-5" />
    </MobileIconButton>
  );

  const trailingButtons = (
    <div className="flex shrink-0 items-center gap-2">
      {filters && (
        <MobileIconButton
          label="Filters"
          badge={activeFilterCount}
          onClick={() => setFiltersOpen(true)}
        >
          <SlidersHorizontal className="h-[18px] w-[18px]" />
        </MobileIconButton>
      )}
      {visibleActions.length > 0 && (
        <MobileIconButton label="More actions" onClick={() => setActionsOpen(true)}>
          <MoreHorizontal className="h-5 w-5" />
        </MobileIconButton>
      )}
    </div>
  );

  const body = (
    <div
      className={cn(
        "space-y-5 px-4 pt-[calc(env(safe-area-inset-top)_+_1rem)]",
        primaryAction ? "pb-28" : "pb-8",
      )}
    >
      <header className="space-y-3">
        {backButton && <div>{backButton}</div>}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-tight font-jakarta text-gray-900">
              {title}
            </h1>
            {description && (
              <p className="mt-1 text-[13px] leading-snug text-gray-500 line-clamp-2">
                {description}
              </p>
            )}
          </div>
          {trailingButtons}
        </div>
        {headerContent}
      </header>

      {stats && stats.length > 0 && <MobileStatsStrip items={stats} />}
      {insights && <MobileInsights>{insights}</MobileInsights>}

      <div className="space-y-4">{children}</div>
    </div>
  );

  return (
    <div className="relative h-full bg-gray-50">
      <div
        className={cn(
          "absolute inset-x-0 top-0 z-20 border-b border-gray-100 bg-white/90 pt-[env(safe-area-inset-top)] backdrop-blur-md transition-all duration-200",
          isCollapsed ? "opacity-100" : "pointer-events-none -translate-y-2 opacity-0",
        )}
      >
        <div className="flex h-14 items-center gap-2 px-4">
          {backButton}
          <span className="min-w-0 flex-1 truncate text-base font-bold font-jakarta text-gray-900">
            {title}
          </span>
          {trailingButtons}
        </div>
      </div>

      {onRefresh ? (
        <PullToRefresh onRefresh={async () => { await onRefresh(); }} onScroll={handleScroll}>
          {body}
        </PullToRefresh>
      ) : (
        <div
          className="h-full overflow-y-auto overscroll-y-contain"
          onScroll={(event) => handleScroll(event.currentTarget.scrollTop)}
        >
          {body}
        </div>
      )}

      {primaryAction && (
        <FloatingActionButton action={primaryAction} compact={isScrollingDown} />
      )}

      {filters && (
        <BottomSheet
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          title="Filters"
          headerAction={
            onResetFilters && !!activeFilterCount ? (
              <button
                type="button"
                onClick={onResetFilters}
                className="border-0 bg-transparent px-2 text-sm font-semibold text-primary-600 active:opacity-60"
              >
                Reset
              </button>
            ) : undefined
          }
          footer={
            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              className="h-12 w-full rounded-2xl border-0 bg-gray-900 text-sm font-semibold text-white active:bg-gray-800"
            >
              Show results
            </button>
          }
        >
          <div className="space-y-5 px-5 pb-4">{filters}</div>
        </BottomSheet>
      )}

      {visibleActions.length > 0 && (
        <ActionSheet
          open={actionsOpen}
          onClose={() => setActionsOpen(false)}
          title={title}
          actions={visibleActions}
        />
      )}
    </div>
  );
};

export default MobilePageLayout;
